import type Task from '@/entities/task';
import * as TaskHelper from './TaskHelper';

export type OverrideKind = 'cost' | 'laborRate' | 'travelRate' | 'laborTime' | 'travelTime';

const FIELDS = {
    cost: {flag: 'isCostOverridden', value: 'costOverride'},
    laborRate: {flag: 'isLaborRateOverridden', value: 'laborRateOverride'},
    travelRate: {flag: 'isTravelRateOverridden', value: 'travelRateOverride'},
    laborTime: {flag: 'isLaborTimeOverridden', value: 'laborTimeOverride'},
    travelTime: {flag: 'isTravelTimeOverridden', value: 'travelTimeOverride'},
} as const;

/**
 * The fields to change when the user toggles an override on a task (OWA-1656). Turning an override
 * on seeds it with the value the task computes without that override, so switching it on changes
 * nothing until the user types a new value. Turning it off clears the value. Templates already work
 * this way for the cost override.
 *
 * @param {Task} task - the task as it is now
 * @param {OverrideKind} kind - which override was toggled
 * @returns {Partial<Task>} the flag and value fields to apply
 */
export const toggleOverride = (task: Task, kind: OverrideKind): Partial<Task> => {
    const {flag, value} = FIELDS[kind];

    if (task[flag]) return {[flag]: false, [value]: 0};

    // What this task computes with the override off
    const base = task.clone();
    base[flag] = false;

    const seed = kind === 'cost' ? TaskHelper.getCost(base) : TaskHelper.getCostParts(base)[kind];

    return {[flag]: true, [value]: seed};
};

/**
 * The rate fields to set when a task is marked billable. A rate the user overrode keeps its
 * rate; onReassign follows the same rule (OWA-1656).
 *
 * @param {Task} task
 * @param {number} rate - the current billable rate
 * @returns {Partial<Task>}
 */
export const billableRateChanges = (task: Task, rate: number): Partial<Task> => ({
    ...(task.isLaborRateOverridden ? {} : {laborRate: rate}),
    ...(task.isTravelRateOverridden ? {} : {travelRate: rate}),
    associatedRate: rate,
});
